import { Search, Wrench as WrenchIcon, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useState } from 'react';
import wphLogo from '../../../assets/wph-logo.png';
import tauhokoholoLogo from '../../../assets/tauhokohoko-logo.gif';
import hrdaoLogo from '../../../assets/hrdao-logo.jpg';
import ahauLogo from '../../../assets/ahau-logo.png';
import catalystLogo from '../../../assets/project-catalyst-logo.jpeg';
import matouMlogo from '../../../assets/matou-m-logo-teal.svg';
import maramatakaLogo from '../../../assets/maramataka-logo.png';
import tokensLogo from '../../../assets/tokenomics.png';
import natureDbLogo from '../../../assets/nature-db.svg';
import distributedAiLogo from '../../../assets/distributed-ai.svg';
import indiCloudLogo from '../../../assets/indi-cloud.png';
import indiChainLogo from '../../../assets/indigenous-chain.svg';

interface ServiceArea {
  title: string;
  icon: LucideIcon;
  description: string;
}

const serviceAreas: ServiceArea[] = [
  {
    title: 'Research',
    icon: Search,
    description: 'Exploring how indigenous knowledge systems can inform the design of equitable, community-owned technology.',
  },
  {
    title: 'Development',
    icon: WrenchIcon,
    description: 'Building open-source tools that put data ownership and digital sovereignty in the hands of communities.',
  },
  {
    title: 'Workshops',
    icon: Users,
    description: 'Sharing knowledge and building capability so communities can lead their own digital futures.',
  },
];

interface Project {
  name: string;
  icon?: LucideIcon;
  image?: string;
  bgColor?: string;
  iconColor: string;
  borderColor: string;
  imageScale?: number;
  link: string;
  isFuture: boolean;
  description?: string;
  contributors?: string[];
  darkText?: boolean;
  secondaryLink?: string;
  secondaryLinkLabel?: string;
  appStoreLink?: string;
  googlePlayLink?: string;
}

const projects: Project[] = [
  {
    name: 'IDSS',
    image: indiCloudLogo,
    bgColor: '#253D39',
    iconColor: '#253D39',
    borderColor: '#253D39',
    link: 'https://idss-ecosystem.matou.nz/',
    isFuture: false,
    description: 'The Indigenous cloud platform: community-owned hosting and governance infrastructure, so communities can run their own digital systems on infrastructure they control.',
    contributors: ['Ben', 'Ian', 'Cherese', 'Tyrone', 'Engie'],
  },
  {
    name: 'Matou App',
    image: matouMlogo,
    bgColor: '#1e5f74',
    iconColor: '#1e5f74',
    borderColor: '#1e5f74',
    link: 'https://matou.nz/download',
    isFuture: false,
    description: 'A self-hosted community app built on the Matou platform. Provides encrypted messaging, membership management, and governance tools — all running on infrastructure owned by the community.',
    contributors: ['Ben', 'Engie', 'Gabriel'],
  },
  {
    name: 'HRDAO',
    image: hrdaoLogo,
    bgColor: '#ffff01',
    iconColor: '#ffff01',
    borderColor: '#ffff01',
    darkText: true,
    link: '#',
    isFuture: false,
    description: 'A community-driven activism platform built with Amnesty DAO. Enables people to discover human rights campaigns, participate in collective action, and earn rewards to donate to causes they care about.',
    contributors: ['Ben', 'Cherese'],
    secondaryLink: 'https://gitlab.com/matou-collective/amnesty-dao',
    secondaryLinkLabel: 'Source Code',
    appStoreLink: 'https://apps.apple.com/es/app/human-rights-dao/id6755700321',
    googlePlayLink: 'http://play.google.com/store/apps/details?id=nz.matou.hrdao',
  },
  {
    name: 'Indigenous Digital Identity',
    image: tauhokoholoLogo,
    bgColor: '#000000',
    iconColor: '#000000',
    borderColor: '#000000',
    link: 'https://identity.matou.nz/',
    isFuture: false,
    description: 'A digital identity system designed by and for Indigenous communities. Allows individuals to manage their own identity and credentials without relying on centralised authorities.',
    contributors: ['Ben', 'Engie', 'Tyrone'],
    secondaryLink: 'https://gitlab.com/matou-collective/matou-identity',
    secondaryLinkLabel: 'Source Code',
  },
  {
    name: 'WPH Membership System',
    image: wphLogo,
    bgColor: '#FFFFFF',
    iconColor: '#5ebf7e',
    borderColor: '#5ebf7e',
    link: 'https://whangaroapapahapu.org.nz/',
    isFuture: false,
    description: 'A self-hosted membership system developed and deployed for Whangaroa Papa Hapū. Enables the hapū to manage their own member registrations and communications on infrastructure they control.',
    contributors: ['Ben', 'Engie', 'Cherese'],
  },
  {
    name: 'TribalDAO Blueprint',
    image: catalystLogo,
    bgColor: '#0C2340',
    iconColor: '#0C2340',
    borderColor: '#0C2340',
    link: 'https://docs.matou.nz/knowledge/research/tribalDAOs/blueprint/v4/',
    isFuture: false,
    description: 'A blueprint and framework for adapting decentralised autonomous organisations to serve Indigenous governance structures. Includes tokenomics design, governance roles, and a phased implementation roadmap.',
    contributors: ['Ben', 'Engie'],
  },
  {
    name: 'AHAU',
    image: ahauLogo,
    bgColor: '#a90000',
    iconColor: '#a90000',
    borderColor: '#a90000',
    link: 'https://github.com/Ahau-NZ',
    isFuture: false,
    description: 'A whakapapa and storytelling platform that enables communities to record, share, and preserve their genealogy and cultural narratives in a secure, self-hosted environment.',
    contributors: ['Ben', 'Cherese'],
  },
  {
    name: 'Maramataka API',
    image: maramatakaLogo,
    bgColor: '#FFFFFF',
    iconColor: '#000000',
    borderColor: '#000000',
    link: 'https://gitlab.com/matou-collective/maramataka-api',
    secondaryLink: 'https://maramataka-api.matou.nz/',
    secondaryLinkLabel: 'Live API',
    isFuture: false,
    description: 'An API that provides access to the Māori lunar calendar (Maramataka). Designed to integrate traditional knowledge of natural cycles into modern digital tools and applications.',
    contributors: ['Ben'],
  },
  {
    name: 'Tokenomics Model',
    image: tokensLogo,
    bgColor: '#253D39',
    iconColor: '#253D39',
    borderColor: '#253D39',
    link: '#',
    isFuture: true,
  },
  {
    name: 'Nature DB',
    image: natureDbLogo,
    bgColor: '#1a3a5c',
    iconColor: '#1a3a5c',
    borderColor: '#1a3a5c',
    imageScale: 0.75,
    link: '#',
    isFuture: true,
  },
  {
    name: 'Distributed AI',
    image: distributedAiLogo,
    bgColor: '#6B4F9E',
    iconColor: '#6B4F9E',
    borderColor: '#6B4F9E',
    link: '#',
    isFuture: true,
  },
  {
    name: 'Indigenous Chain',
    image: indiChainLogo,
    bgColor: '#1e5f74',
    iconColor: '#1e5f74',
    borderColor: '#1e5f74',
    link: '#',
    isFuture: true,
  },
];

export default function Services() {
  const [modalProject, setModalProject] = useState<Project | null>(null);

  return (
    <section id="services" className="py-16 md:py-24 bg-matou-bg">
      <div className="max-w-content mx-auto px-4 md:px-8">
        {/* Services */}
        <div className="bg-matou-teal-deep rounded-t-md px-5 py-3">
          <h2 className="font-heading text-white text-sm font-bold uppercase tracking-widest">
            Services
          </h2>
        </div>
        <div className="border border-t-0 border-black/10 rounded-b-md p-6 md:p-8 bg-matou-surface mb-8">
          <p className="font-mono text-sm text-matou-text-muted leading-relaxed mb-8 max-w-4xl">
            It's important for us to share what we know and to support others where we can. If your organisation or community is looking for a partner grounded in indigenous values to support your digital aspirations, we're available and keen to kōrero.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {serviceAreas.map((area) => {
              const Icon = area.icon;
              return (
                <div key={area.title} className="bg-matou-bg border border-black/10 rounded-lg p-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-matou-teal-soft/20 flex items-center justify-center mx-auto mb-4">
                    <Icon size={28} className="text-matou-teal-medium" strokeWidth={0.5} />
                  </div>
                  <h3 className="font-heading text-base font-bold text-matou-text mb-2">
                    {area.title}
                  </h3>
                  <p className="font-mono text-xs text-matou-text-muted leading-relaxed">
                    {area.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Projects */}
        <div id="products" className="bg-matou-teal-deep rounded-t-md px-5 py-3">
          <h2 className="font-heading text-white text-sm font-bold uppercase tracking-widest">
            Projects
          </h2>
        </div>
        <div className="border border-t-0 border-black/10 rounded-b-md p-6 md:p-8 bg-matou-surface">
          <p className="font-mono text-sm text-matou-text-muted leading-relaxed mb-8 max-w-4xl">
            These are the projects we've built, are building, or are planning to build. Each one is driven by a real need from our communities and grounded in the values of indigenous data sovereignty, open-source collaboration, and collective ownership.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 md:gap-6">
            {projects.slice(0, 6).map((project, i) => (
              <ProjectCard key={i} project={project} onClick={() => setModalProject(project)} />
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 md:gap-6 mt-4 md:mt-6">
            {projects.slice(6, 12).map((project, i) => (
              <ProjectCard key={i + 6} project={project} onClick={() => setModalProject(project)} />
            ))}
          </div>
          {projects.length > 12 && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 md:gap-6 mt-4 md:mt-6">
            {projects.slice(12).map((project, i) => (
              <ProjectCard key={i + 12} project={project} onClick={() => setModalProject(project)} />
            ))}
          </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {modalProject && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setModalProject(null)}
        >
          <div
            className="bg-matou-surface rounded-xl shadow-2xl max-w-lg w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with logo and title */}
            <div
              className="flex items-center gap-4 p-6"
              style={{ backgroundColor: modalProject.bgColor || modalProject.borderColor }}
            >
              {modalProject.image && (
                <div className="h-14 w-14 rounded-lg overflow-hidden bg-white/10 shrink-0">
                  <img src={modalProject.image} alt="" className="h-full w-full object-contain" />
                </div>
              )}
              <h3 className={`font-heading text-xl font-bold flex-1 ${modalProject.darkText ? 'text-black' : 'text-white'}`}>
                {modalProject.name}
              </h3>
              <button
                onClick={() => setModalProject(null)}
                className={`${modalProject.darkText ? 'text-black/70 hover:text-black' : 'text-white/70 hover:text-white'} transition-colors shrink-0`}
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              {/* Description */}
              {modalProject.description && (
                <p className="font-mono text-sm text-matou-text-muted leading-relaxed mb-6">
                  {modalProject.description}
                </p>
              )}

              {/* Contributors */}
              {modalProject.contributors && modalProject.contributors.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-mono text-xs text-matou-text-muted uppercase tracking-wide mb-2">Contributors</h4>
                  <div className="flex flex-wrap gap-2">
                    {modalProject.contributors.map((name) => (
                      <span
                        key={name}
                        className="font-mono text-xs px-3 py-1 rounded-full bg-matou-bg text-matou-text"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Links */}
              <div className="flex flex-wrap gap-3">
                {modalProject.appStoreLink && (
                  <a
                    href={modalProject.appStoreLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-block font-mono text-sm px-4 py-2 rounded-md transition-colors duration-150 ${modalProject.darkText ? 'text-black' : 'text-white'}`}
                    style={{ backgroundColor: modalProject.borderColor }}
                  >
                    App Store
                  </a>
                )}
                {modalProject.googlePlayLink && (
                  <a
                    href={modalProject.googlePlayLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-block font-mono text-sm px-4 py-2 rounded-md transition-colors duration-150 ${modalProject.darkText ? 'text-black' : 'text-white'}`}
                    style={{ backgroundColor: modalProject.borderColor }}
                  >
                    Google Play
                  </a>
                )}
                {modalProject.link && modalProject.link !== '#' && (
                  <a
                    href={modalProject.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-block font-mono text-sm px-4 py-2 rounded-md transition-colors duration-150 ${modalProject.darkText ? 'text-black' : 'text-white'}`}
                    style={{ backgroundColor: modalProject.borderColor }}
                  >
                    View Project →
                  </a>
                )}
                {modalProject.secondaryLink && (
                  <a
                    href={modalProject.secondaryLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-block font-mono text-sm px-4 py-2 rounded-md border-2 transition-colors duration-150 ${modalProject.darkText ? 'text-black' : 'text-white'}`}
                    style={{ backgroundColor: modalProject.borderColor, borderColor: modalProject.borderColor }}
                  >
                    {modalProject.secondaryLinkLabel || 'View Link'}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function ProjectCard({ project, onClick }: { project: Project; onClick: () => void }) {
  const Icon = project.icon ?? null;
  const hasImageBg = project.image && project.bgColor;
  const [shaking, setShaking] = useState(false);

  const handleClick = () => {
    if (project.isFuture) {
      setShaking(true);
      setTimeout(() => setShaking(false), 400);
    } else {
      onClick();
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`group relative rounded-lg text-center transition-all duration-150 cursor-pointer flex flex-col items-center justify-center overflow-hidden ${
        shaking ? 'animate-shake' : ''
      } ${
        hasImageBg
          ? 'p-0 hover:-translate-y-0.5'
          : project.isFuture
            ? 'p-4 md:p-6 bg-white hover:-translate-y-0.5'
            : 'p-4 md:p-6 bg-white hover:-translate-y-0.5'
      }`}
      style={{
        border: `2px solid ${hasImageBg ? project.borderColor : project.isFuture ? project.borderColor : '#000000'}`,
        backgroundColor: hasImageBg ? project.bgColor : undefined,
      }}
    >
      <div aria-hidden="true" className="transition-transform duration-200 group-hover:-translate-y-1.5">
        {hasImageBg ? (
          <img
            src={project.image}
            alt=""
            className="w-full object-contain"
            style={{ height: `${80 * (project.imageScale ?? 1)}px` }}
          />
        ) : Icon ? (
          <Icon size={48} color={project.isFuture ? '#555555' : '#000000'} strokeWidth={0.5} />
        ) : null}
      </div>
      {(project.name || shaking) && (
        <div className={`absolute bottom-0 left-0 right-0 bg-black/90 transition-transform duration-200 ease-out ${
          shaking ? 'translate-y-0' : 'translate-y-full group-hover:translate-y-0'
        }`}>
          <p className="font-mono text-xs text-white py-2 px-2 truncate">
            {shaking ? 'Coming Soon' : project.name}
          </p>
        </div>
      )}
    </button>
  );
}
